import type { DocumentWorkflowStepDto } from '../services/documentWorkflowService';
import type { WorkflowStep } from '../services/workflowService';
import { isArchiveStepType } from './workflowConstants';

export type WorkflowBannerTimelineStep = {
  id: string;
  name: string;
  dateLabel: string | null;
  state: 'done' | 'current' | 'pending';
};

export type WorkflowBannerTimelineBuild = {
  timelineSteps: WorkflowBannerTimelineStep[];
  currentStepIndex: number;
};

function normalizeWorkflowGuid(value: string | null | undefined): string {
  return (value || '').trim().toLowerCase();
}

function isOpenDocumentWorkflowStep(status: string | undefined): boolean {
  const s = (status || '').toLowerCase();
  return s !== 'completed' && s !== 'rejected' && s !== 'cancelled';
}

function resolveCurrentTemplateStepIndex(
  orderedTemplate: WorkflowStep[],
  docStep: DocumentWorkflowStepDto
): number {
  const docWid = normalizeWorkflowGuid(docStep.workflowStepId);
  if (docWid) {
    const byId = orderedTemplate.findIndex(t => normalizeWorkflowGuid(t.id) === docWid);
    if (byId >= 0) return byId;
  }
  if (docStep.definitionOrder != null) {
    const byOrder = orderedTemplate.findIndex(t => t.order === docStep.definitionOrder);
    if (byOrder >= 0) return byOrder;
  }
  const defType = (docStep.definitionType || '').trim().toLowerCase();
  if (defType) {
    const byType = orderedTemplate.findIndex(
      t => (t.type || '').trim().toLowerCase() === defType
    );
    if (byType >= 0) return byType;
  }
  return -1;
}

function latestActivityIsoForTemplateStep(
  instances: DocumentWorkflowStepDto[],
  workflowStepId: string
): string | null {
  const want = normalizeWorkflowGuid(workflowStepId);
  let best: string | null = null;
  for (const s of instances) {
    if (normalizeWorkflowGuid(s.workflowStepId) !== want) continue;
    const cand = s.reviewedAt || s.startedAt || s.createdAt;
    if (!cand) continue;
    if (!best || new Date(cand).getTime() > new Date(best).getTime()) best = cand;
  }
  return best;
}

export function friendlyWorkflowStepType(type: string | null | undefined): string {
  if (!type?.trim()) return '';
  const n = type.trim();
  const pairs: [string, string][] = [
    ['Edit', 'Edição'],
    ['Review', 'Revisão'],
    ['Signature', 'Assinatura'],
    ['Publish', 'Publicação'],
    ['Archive', 'Arquivamento'],
  ];
  const hit = pairs.find(([en]) => en.localeCompare(n, undefined, { sensitivity: 'accent' }) === 0);
  return hit ? hit[1] : n;
}

export function orderWorkflowTemplateSteps(steps: WorkflowStep[] | undefined | null): WorkflowStep[] {
  if (!steps?.length) return [];
  return [...steps].sort((a, b) => a.order - b.order);
}

export function buildWorkflowBannerTimelineFromTemplate(
  templateSteps: WorkflowStep[],
  docStep: DocumentWorkflowStepDto,
  instances: DocumentWorkflowStepDto[]
): WorkflowBannerTimelineBuild {
  const ordered = orderWorkflowTemplateSteps(templateSteps).filter(s => !isArchiveStepType(s.type));
  if (ordered.length === 0) {
    return { timelineSteps: [], currentStepIndex: 0 };
  }

  // Quando o documento está na etapa de Arquivamento, todas as etapas visíveis já foram concluídas.
  if (isArchiveStepType(docStep.definitionType)) {
    const timelineSteps: WorkflowBannerTimelineStep[] = ordered.map((tplStep, index) => {
      const displayIso = latestActivityIsoForTemplateStep(instances, tplStep.id);
      const dateLabel = displayIso
        ? new Date(displayIso).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })
        : null;
      const name =
        (tplStep.name && tplStep.name.trim()) ||
        friendlyWorkflowStepType(tplStep.type) ||
        `Etapa ${index + 1}`;
      return { id: tplStep.id, name, dateLabel, state: 'done' as const };
    });
    return { timelineSteps, currentStepIndex: ordered.length - 1 };
  }

  let curIdx = resolveCurrentTemplateStepIndex(ordered, docStep);
  if (curIdx < 0 && typeof docStep.stepOrder === 'number' && docStep.stepOrder >= 1) {
    curIdx = Math.min(docStep.stepOrder - 1, ordered.length - 1);
  }
  if (curIdx < 0) {
    const sortedInst = [...instances].sort((a, b) => a.stepOrder - b.stepOrder);
    const openIdx = sortedInst.findIndex(s => isOpenDocumentWorkflowStep(s.status));
    if (openIdx >= 0) curIdx = Math.min(openIdx, ordered.length - 1);
  }
  if (curIdx < 0) curIdx = 0;

  const currentOrder = ordered[curIdx].order;
  const timelineSteps: WorkflowBannerTimelineStep[] = ordered.map((tplStep, index) => {
    const displayIso = latestActivityIsoForTemplateStep(instances, tplStep.id);
    const dateLabel = displayIso
      ? new Date(displayIso).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })
      : null;
    const name =
      (tplStep.name && tplStep.name.trim()) ||
      friendlyWorkflowStepType(tplStep.type) ||
      `Etapa ${index + 1}`;

    let state: 'done' | 'current' | 'pending';
    if (tplStep.order < currentOrder) state = 'done';
    else if (tplStep.order === currentOrder) state = 'current';
    else state = 'pending';
    return { id: tplStep.id, name, dateLabel, state };
  });

  return { timelineSteps, currentStepIndex: curIdx };
}
